/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IAdminVocabularyBookData, VOCABULARY_BOOK_LEVEL_LABEL } from '@datatype/admin/api-type';
import Chip from '@design-system/chip';
import TextAudioButton from '@design-system/text-audio-button';

interface IProps {
  selectedBook: IAdminVocabularyBookData | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  TEST: '시험준비',
  STUDY: '일반학습',
};

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  padding: 20px;
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
  background-color: ${color.WHITE_1};
`;

const Empty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  font-size: 13px;
  color: ${color.BLACK_5};
`;

const ChipGroup = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 6px;
  align-items: center;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${color.BLACK_2};
`;

const Description = styled.div`
  font-size: 13px;
  color: ${color.BLACK_4};
`;

const KeyText = styled.div`
  font-size: 11px;
  color: ${color.BLACK_5};
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${color.BLACK_3};
  padding-top: 8px;
  border-top: 1px dashed ${color.GRAY_3};
`;

const WordList = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
`;

const WordRow = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${color.GRAY_3};
  border-radius: 6px;
`;

const WordBody = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 4px;
`;

const SourceLine = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 8px;
  align-items: baseline;
`;

const Source = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${color.BLACK_2};
`;

const Pronunciation = styled.span`
  font-size: 12px;
  color: ${color.BLACK_5};
`;

const MeaningRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 8px;
  font-size: 12px;
  color: ${color.BLACK_3};
`;

const Lang = styled.span`
  font-weight: 600;
  color: ${color.BLACK_5};
`;

const formatDate = (iso: string | null): string => {
  if (!iso) return '-';
  return iso.slice(0, 10);
};

const VocabularyBookPreview: React.FC<IProps> = ({ selectedBook }) => {
  if (!selectedBook) {
    return <Empty>좌측에서 단어집을 선택하세요</Empty>;
  }

  const categoryLabel = CATEGORY_LABEL[selectedBook.category] ?? selectedBook.category;
  const levelLabel = VOCABULARY_BOOK_LEVEL_LABEL[selectedBook.level] ?? selectedBook.level;
  const title =
    selectedBook.name ?? `${selectedBook.languageCode} ${categoryLabel} ${levelLabel} ${selectedBook.order}탄`;

  return (
    <Wrapper>
      <ChipGroup>
        <Chip text={selectedBook.languageCode} size='tiny' isSelected theme={ETheme.BROWN} />
        <Chip text={categoryLabel} size='tiny' isSelected theme={ETheme.GRAY} />
        <Chip text={levelLabel} size='tiny' isSelected theme={ETheme.YELLOW} />
        <Chip text={`order ${selectedBook.order}`} size='tiny' kind='outlined' isSelected theme={ETheme.GRAY} />
        <Chip text={`${selectedBook.itemCount}개`} size='tiny' kind='outlined' isSelected theme={ETheme.GRAY} />
        <Chip
          text={selectedBook.publishedAt ? `Published At: ${formatDate(selectedBook.publishedAt)}` : '미발행'}
          size='tiny'
          isSelected
          theme={selectedBook.publishedAt ? ETheme.GREEN : ETheme.RED}
        />
      </ChipGroup>
      <Title>{title}</Title>
      {selectedBook.description && <Description>{selectedBook.description}</Description>}
      <KeyText>key: {selectedBook.key}</KeyText>

      <SectionTitle>단어 ({selectedBook.bundles.length})</SectionTitle>
      <WordList>
        {selectedBook.bundles.map((bundle) => {
          const pronunciation = (bundle.aiVocabulary.sourcePronunciation ?? []).filter(Boolean).join(' / ');
          const meaningKo = (bundle.aiVocabulary.meaningKo ?? []).filter(Boolean).join(', ');
          const meaningEn = (bundle.aiVocabulary.meaningEn ?? []).filter(Boolean).join(', ');
          const meaningJa = (bundle.aiVocabulary.meaningJa ?? []).filter(Boolean).join(', ');
          return (
            <WordRow key={bundle.key}>
              <SourceLine>
                <Chip text={`order ${bundle.order}`} size='tiny' kind='outlined' isSelected theme={ETheme.GRAY} />
                <Source>{bundle.aiVocabulary.source}</Source>
                {pronunciation && <Pronunciation>[{pronunciation}]</Pronunciation>}
                {bundle.aiVocabulary.wordClass && (
                  <Chip
                    text={bundle.aiVocabulary.wordClass}
                    size='tiny'
                    kind='outlined'
                    isSelected
                    theme={ETheme.GRAY}
                  />
                )}
                {bundle.aiVocabulary.sourceAudioLink && bundle.aiVocabulary.sourceAudioLink.length > 0 && (
                  <TextAudioButton
                    sources={bundle.aiVocabulary.sourceAudioLink}
                    size='small'
                    kind='outlined'
                    shape='round'
                    width='72px'
                  />
                )}
              </SourceLine>
              <WordBody>
                {meaningKo && (
                  <MeaningRow>
                    <Lang>KO</Lang>
                    <span>{meaningKo}</span>
                  </MeaningRow>
                )}
                {meaningEn && (
                  <MeaningRow>
                    <Lang>EN</Lang>
                    <span>{meaningEn}</span>
                  </MeaningRow>
                )}
                {meaningJa && (
                  <MeaningRow>
                    <Lang>JA</Lang>
                    <span>{meaningJa}</span>
                  </MeaningRow>
                )}
              </WordBody>
            </WordRow>
          );
        })}
      </WordList>
    </Wrapper>
  );
};

export default VocabularyBookPreview;
